/**
 * Il profilo energetico di un socio: cosa manca perché sia completo.
 *
 * Al posto dei documenti, che l'app non raccoglie più, lo stato del socio dice
 * se sappiamo com'è fatta la sua energia. Ogni requisito viene da ciò che il
 * GSE chiede per lo stesso oggetto:
 *
 * - **POD** su ogni punto di prelievo: è l'identità del punto di misura
 *   (Regole Operative CACER).
 * - **Tipologia di utenza** su ogni punto di prelievo: i profili standard sono
 *   definiti per tipologia di cliente finale TIT (Allegato 1 § 2.2.3). Un
 *   profilo GSE scelto a mano sul punto (`gseProfileCode`) la sostituisce,
 *   perché è già quella scelta.
 * - **Impianto**, con **moduli** (numero e potenza del modulo) e **inverter**
 *   (potenza): campi obbligatori delle sezioni «Moduli» e «Inverter» della
 *   Guida SPC GSE.
 * - **Batteria**, capacità e potenza, per chi dichiara un accumulo: le RO
 *   (§ 1.1.5) chiedono le informazioni tecniche necessarie a caratterizzarlo.
 *
 * Bollette e storici non entrano: sono facoltativi, migliorano le curve ma non
 * descrivono il punto.
 */

import type { MemberAppliance } from './appliances';

export type EnergyProfileGap =
	| 'pod'
	| 'tipologia'
	| 'impianto'
	| 'moduli'
	| 'inverter'
	| 'batteria';

export const ENERGY_PROFILE_GAP_LABELS: Record<EnergyProfileGap, string> = {
	pod: 'Codice POD',
	tipologia: 'Tipo di utenza',
	impianto: 'Impianto di produzione',
	moduli: 'Moduli (numero e potenza)',
	inverter: 'Potenza inverter',
	batteria: 'Batteria (capacità e potenza)',
};

export type TipologiaUtenza = 'domestico' | 'altri_usi' | 'ricarica_veicoli';

export const TIPOLOGIA_UTENZA_LABELS: Record<TipologiaUtenza, string> = {
	domestico: 'Domestico',
	altri_usi: 'Altri usi (attività, servizi)',
	ricarica_veicoli: 'Ricarica veicoli elettrici',
};

export interface EnergyProfileInput {
	ruolo: 'consumer' | 'producer' | 'prosumer';
	hasStorage?: boolean | null;
	pods: { id: string; codice?: string | null; tipologiaUtenza?: string | null }[];
	meters: {
		id: string;
		podId?: string | null;
		tipo: 'withdrawal' | 'injection' | 'storage' | string;
		gseProfileCode?: string | null;
		capacitaKwh?: number | null;
		potenzaKw?: number | null;
	}[];
	plants: {
		id: string;
		meterId: string;
		numeroModuli?: number | null;
		potenzaModuloW?: number | null;
	}[];
	inverters: { plantId: string; potenzaKw?: number | null }[];
}

const positive = (n: number | null | undefined): boolean =>
	typeof n === 'number' && Number.isFinite(n) && n > 0;

/** I dati che mancano al profilo del socio, in ordine di scheda. Vuoto = completo. */
export function energyProfileGaps(input: EnergyProfileInput): EnergyProfileGap[] {
	const gaps: EnergyProfileGap[] = [];
	const podById = new Map(input.pods.map((p) => [p.id, p]));
	const consuma = input.ruolo === 'consumer' || input.ruolo === 'prosumer';
	const produce = input.ruolo === 'producer' || input.ruolo === 'prosumer';

	const prelievi = input.meters.filter((m) => m.tipo === 'withdrawal');
	const podOf = (m: { podId?: string | null }) => (m.podId ? podById.get(m.podId) : undefined);

	const conPod = (consuma ? prelievi : input.meters).filter((m) => podOf(m)?.codice?.trim());
	if (conPod.length === 0 || (consuma && conPod.length < prelievi.length)) gaps.push('pod');

	if (
		consuma &&
		(prelievi.length === 0 || prelievi.some((m) => !m.gseProfileCode && !podOf(m)?.tipologiaUtenza))
	)
		gaps.push('tipologia');

	if (produce) {
		const immissioni = new Set(input.meters.filter((m) => m.tipo === 'injection').map((m) => m.id));
		const plants = input.plants.filter((p) => immissioni.has(p.meterId));
		if (plants.length === 0) gaps.push('impianto');
		else {
			if (plants.some((p) => !positive(p.numeroModuli) || !positive(p.potenzaModuloW)))
				gaps.push('moduli');
			if (
				plants.some(
					(p) => !input.inverters.some((i) => i.plantId === p.id && positive(i.potenzaKw)),
				)
			)
				gaps.push('inverter');
		}
	}

	// L'accumulo sta accanto a un impianto: un consumatore puro non ne ha, anche
	// se una riga vecchia dice il contrario.
	if (input.hasStorage && input.ruolo !== 'consumer') {
		const batterie = input.meters.filter((m) => m.tipo === 'storage');
		if (
			batterie.length === 0 ||
			batterie.some((m) => !positive(m.capacitaKwh) || !positive(m.potenzaKw))
		)
			gaps.push('batteria');
	}

	return gaps;
}

/**
 * Lo stato da mostrare. Recesso e subentro sono passaggi decisi dall'admin e
 * restano; per tutti gli altri lo stato è il profilo: completo o incompleto.
 */
export function memberStatusFromProfile<S extends string>(
	stato: S,
	gaps: readonly EnergyProfileGap[],
): S | 'verified' | 'incomplete' {
	if (stato === 'withdrawn' || stato === 'takeover') return stato;
	return gaps.length === 0 ? 'verified' : 'incomplete';
}

/**
 * Il profilo energetico visto dal socio stesso (`GET /api/members/me/energy-profile`).
 * Una sezione per punto di misura che il suo ruolo prevede, `null` se non c'è.
 */
export interface OwnEnergyProfile {
	ruolo: 'consumer' | 'producer' | 'prosumer';
	hasStorage: boolean;
	mancanti: EnergyProfileGap[];
	consumo: {
		codice: string | null;
		tipologiaUtenza: TipologiaUtenza | null;
		dispositivi: MemberAppliance[];
	} | null;
	produzione: {
		/** L'impianto lo registra l'amministratore: senza, moduli e inverter non hanno dove stare. */
		haImpianto: boolean;
		potenzaKwp: number | null;
		numeroModuli: number | null;
		potenzaModuloW: number | null;
		potenzaInverterKw: number | null;
		/** Con più di un inverter la potenza è una somma e non si modifica dal socio. */
		inverterCount: number;
	} | null;
	accumulo: { capacitaKwh: number | null; potenzaKw: number | null } | null;
}

/** Cosa il socio può scrivere del proprio profilo: niente oltre questi campi. */
export interface OwnEnergyProfilePatch {
	consumo?: {
		codice?: string | null;
		tipologiaUtenza?: TipologiaUtenza | null;
		/** Sostituisce l'elenco intero; sta sul socio, non sul punto di misura. */
		dispositivi?: MemberAppliance[];
	};
	produzione?: {
		numeroModuli?: number | null;
		potenzaModuloW?: number | null;
		potenzaInverterKw?: number | null;
	};
	accumulo?: { capacitaKwh?: number | null; potenzaKw?: number | null };
}

/** I campi scrivibili dal socio per sezione, in chiave del DTO del misuratore. */
export const OWN_PROFILE_FIELDS = {
	consumo: ['codice', 'tipologiaUtenza'],
	produzione: ['numeroModuli', 'potenzaModuloW', 'potenzaInverterKw'],
	accumulo: ['capacitaKwh', 'potenzaKw'],
} as const;

/**
 * Il profilo come lo scrive l'amministratore (import CSV): quello del socio più
 * potenza e data di esercizio, che servono a creare l'impianto se non c'è.
 */
export interface AdminEnergyProfilePatch extends OwnEnergyProfilePatch {
	produzione?: OwnEnergyProfilePatch['produzione'] & {
		/** Il POD del punto di immissione: per un prosumer è lo stesso del prelievo. */
		codice?: string | null;
		potenzaKwp?: number | null;
		dataEntrataEsercizio?: string | null;
	};
}

/** I campi scrivibili dall'amministratore per sezione. */
export const ADMIN_PROFILE_FIELDS = {
	consumo: OWN_PROFILE_FIELDS.consumo,
	produzione: [...OWN_PROFILE_FIELDS.produzione, 'codice', 'potenzaKwp', 'dataEntrataEsercizio'],
	accumulo: OWN_PROFILE_FIELDS.accumulo,
} as const;
